import React, { useMemo, useState } from "react";
import { useSettlementLedger } from "../store/settlementLedger";

type EntryKind = "TRANSFER" | "PENALTY" | "MINT" | "BURN";

/**
 * Settlement Panel (v2) - Production-style UX
 * 
 * Enhanced UI for ESG Settlement Ledger with:
 * - Better layout for balances (table with status colors)
 * - Better layout for entries (latest 20 + "view all")
 * - Export buttons (JSON + CSV)
 * - Clean, EVAT-style look (dark, neon-ish, Orbitron-friendly)
 */
export function SettlementPanel() {
  const ledger = useSettlementLedger();
  const [kind, setKind] = useState<EntryKind>("TRANSFER");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState("");
  const [showAll, setShowAll] = useState(false);

  const entries = ledger.entries || [];
  const balances = ledger.balances || {};

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => b.ts - a.ts),
    [entries]
  );

  const latestEntries = showAll ? sortedEntries : sortedEntries.slice(0, 20);

  const totalEntities = Object.keys(balances).length;
  const totalEntries = entries.length;

  const highRiskCount = Object.values(balances).filter((v) => v < -5000).length;

  function statusForBalance(v: number) {
    if (v < -5000) return { label: "High deficit", className: "text-red-400" };
    if (v < 0) return { label: "Slight deficit", className: "text-yellow-400" };
    if (v === 0) return { label: "Neutral", className: "text-gray-300" };
    return { label: "Surplus", className: "text-emerald-400" };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!amount || amount <= 0) return;

    if (kind === "TRANSFER") {
      if (!from || !to) return;
      ledger.recordTransfer(from, to, amount, reason || "TRANSFER", "SCOPE1");
    } else if (kind === "PENALTY") {
      if (!from) return;
      ledger.applyPenalty(from, amount, reason || "PENALTY");
    } else if (kind === "MINT") {
      if (!to) return;
      ledger.mintCredit(to, amount, { reason: reason || "MINT" });
    } else if (kind === "BURN") {
      if (!from) return;
      ledger.burnCredit(from, amount, { reason: reason || "BURN" });
    }

    // reset simple fields
    setAmount(0);
    setReason("");
    setFrom("");
    setTo("");
  }

  function exportJSON() {
    const data = {
      version: "1.0",
      exportedAt: new Date().toISOString(),
      entries,
      balances,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `settlement-ledger-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportCSV() {
    if (!entries.length) return;
    const header = [
      "id",
      "ts",
      "from",
      "to",
      "amount",
      "scope",
      "reason",
      "sovereignOverride",
    ];
    const rows = entries.map((e) => [
      e.id,
      new Date(e.ts).toISOString(),
      e.from ?? "",
      e.to ?? "",
      e.amount,
      e.scope,
      e.reason,
      e.sovereignOverride ? "1" : "0",
    ]);
    const csv =
      [header.join(",")]
        .concat(rows.map((r) => r.join(",")))
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `settlement-ledger-${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 md:p-8 space-y-6 bg-black text-neutral-100 font-orbitron">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-wide text-evat-neon">
            ESG Settlement Ledger
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            CO₂ / ESG pénzügyi elszámolás – entitások közötti átvezetések, büntetések, jóváírások.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            className="px-3 py-2 rounded bg-evat-neon/20 border border-evat-neon text-evat-neon hover:bg-evat-neon/30 transition-colors text-sm font-orbitron"
            onClick={exportJSON}
          >
            Export JSON
          </button>
          <button
            className="px-3 py-2 rounded bg-evat-neon/20 border border-evat-neon text-evat-neon hover:bg-evat-neon/30 transition-colors text-sm font-orbitron"
            onClick={exportCSV}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Entities</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">{totalEntities}</div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Entries</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">{totalEntries}</div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">High-Risk Entities</div>
          <div className="text-2xl font-semibold mt-1 text-red-400">
            {highRiskCount}
          </div>
        </div>
      </div>

      {/* New entry form */}
      <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4 space-y-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="font-semibold text-evat-neon">New ledger entry</div>
          <div className="flex gap-2 text-xs">
            {(["TRANSFER", "PENALTY", "MINT", "BURN"] as EntryKind[]).map(
              (k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`px-3 py-1 rounded border text-xs font-orbitron ${
                    kind === k
                      ? "bg-evat-neon/20 border-evat-neon text-evat-neon"
                      : "border-evat-gold/40 text-gray-300 hover:border-evat-gold/60"
                  }`}
                >
                  {k}
                </button>
              )
            )}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
        >
          {(kind === "TRANSFER" || kind === "PENALTY" || kind === "BURN") && (
            <input
              className="px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon placeholder-gray-500 text-sm font-orbitron"
              placeholder="From entity"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          )}
          {(kind === "TRANSFER" || kind === "MINT") && (
            <input
              className="px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon placeholder-gray-500 text-sm font-orbitron"
              placeholder="To entity"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
          <input
            className="px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon placeholder-gray-500 text-sm font-orbitron"
            type="number"
            placeholder="Amount"
            value={amount || ""}
            onChange={(e) => setAmount(Number(e.target.value))}
          />
          <input
            className="px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon placeholder-gray-500 text-sm font-orbitron"
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="submit"
            className="px-4 py-2 rounded bg-evat-neon/20 border border-evat-neon text-evat-neon hover:bg-evat-neon/30 transition-colors text-sm font-semibold font-orbitron"
          >
            Add entry
          </button>
        </form>
      </div>

      {/* Balances table */}
      <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-evat-neon">Balances</h2>
          <span className="text-xs text-gray-400">
            Positive = surplus, negative = deficit
          </span>
        </div>
        {Object.keys(balances).length === 0 ? (
          <div className="text-sm text-gray-400">No balances yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-400 border-b border-evat-gold/40">
                <tr>
                  <th className="text-left py-2 pr-2">Entity</th>
                  <th className="text-right py-2 px-2">Balance</th>
                  <th className="text-left py-2 pl-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(balances)
                  .sort(([, a], [, b]) => {
                    const aVal = typeof a === "number" ? a : 0;
                    const bVal = typeof b === "number" ? b : 0;
                    return bVal - aVal;
                  })
                  .map(([entity, value]) => {
                    const v = typeof value === "number" ? value : 0;
                    const s = statusForBalance(v);
                    return (
                      <tr key={entity} className="border-b border-white/5 hover:bg-black/30">
                        <td className="py-1 pr-2 font-mono text-xs md:text-sm text-evat-neon">
                          {entity}
                        </td>
                        <td className={`py-1 px-2 text-right font-orbitron ${
                          v >= 0 ? "text-evat-neon" : v < -5000 ? "text-red-400" : "text-yellow-400"
                        }`}>
                          {v.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className={`py-1 pl-2 text-xs ${s.className}`}>
                          {s.label}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Entries list */}
      <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-evat-neon">Entries</h2>
          <button
            className="text-xs text-evat-neon underline hover:text-evat-gold transition-colors font-orbitron"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show latest 20" : `Show all (${entries.length})`}
          </button>
        </div>
        {latestEntries.length === 0 ? (
          <div className="text-sm text-gray-400">No entries yet.</div>
        ) : (
          <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
            <table className="w-full text-xs md:text-sm">
              <thead className="text-xs text-gray-400 border-b border-evat-gold/40 sticky top-0 bg-black/80">
                <tr>
                  <th className="text-left py-2 pr-2">Time</th>
                  <th className="text-left py-2 px-2">From</th>
                  <th className="text-left py-2 px-2">To</th>
                  <th className="text-right py-2 px-2">Amount</th>
                  <th className="text-left py-2 px-2">Scope</th>
                  <th className="text-left py-2 pl-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {latestEntries.map((e) => (
                  <tr key={e.id} className="border-b border-white/5 hover:bg-black/30">
                    <td className="py-1 pr-2 whitespace-nowrap text-[11px] text-gray-400">
                      {new Date(e.ts).toLocaleString()}
                    </td>
                    <td className="py-1 px-2 font-mono text-xs">{e.from || "-"}</td>
                    <td className="py-1 px-2 font-mono text-xs">{e.to || "-"}</td>
                    <td className={`py-1 px-2 text-right font-orbitron ${
                      e.amount >= 0 ? "text-evat-neon" : "text-red-400"
                    }`}>
                      {e.amount >= 0 ? "+" : ""}{e.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-1 px-2 text-gray-300">{e.scope}</td>
                    <td className="py-1 pl-2 text-gray-400">{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
